import React, { useState, useRef, useCallback, useMemo } from "react";
import clsx from "clsx";
import { NavLink } from "react-router-dom";

export interface NavItem {
  /** Canonical route path — used for grouping, active-state matching, and as the React key. Keep it free of hashes/query. */
  path: string;
  /** Optional explicit navigation target (e.g. a deep-link with a hash). Defaults to `path`. */
  to?: string;
  label: string;
  icon: React.ReactNode;
  /** Optional count badge rendered on the nav link (only shown when > 0). */
  badge?: number;
  /** Tooltip text for the badge element. */
  badgeTitle?: string;
}

export interface HeaderNavGroupProps {
  /** Unique identifier for the group */
  groupId: string;
  /** Navigation items in this group */
  items: NavItem[];
  /** Color configuration for the group */
  colorConfig: {
    active: string;
    inactive: string;
    divider: string;
  };
  /** Current pathname for active state detection */
  currentPath: string;
  /** Base class for nav buttons */
  navButtonBaseClass: string;
  /** Show icons on larger screens */
  showIcons?: boolean;
}

// Timing constants for hover debouncing
const HOVER_ENTER_DELAY_MS = 50;
const HOVER_LEAVE_DELAY_MS = 200;

// Chevron icon component
const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 20 20" 
    fill="currentColor"
    aria-hidden="true"
  >
    <path 
      fillRule="evenodd" 
      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" 
      clipRule="evenodd" 
    />
  </svg>
);

/**
 * HeaderNavGroup - An animated navigation group component that expands on hover
 * 
 * Features:
 * - Smooth width expansion animation on hover
 * - Chevron indicator showing more items available
 * - Staggered reveal of sub-items
 * - Debounced hover state to prevent flickering
 * - GPU-accelerated animations via CSS transforms
 * - Preserves keyboard accessibility
 */
const HeaderNavGroup: React.FC<HeaderNavGroupProps> = ({
  groupId,
  items,
  colorConfig,
  currentPath,
  navButtonBaseClass,
  showIcons = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const enterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const leaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeouts on unmount
  const clearTimeouts = useCallback(() => {
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Clear any pending leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    
    // Set a small delay before expanding to avoid accidental triggers
    enterTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, HOVER_ENTER_DELAY_MS);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Clear any pending enter timeout
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
    
    // Delay collapse to allow cursor to move between items
    leaveTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, HOVER_LEAVE_DELAY_MS);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => clearTimeouts();
  }, [clearTimeouts]);

  // Count of hidden items
  const hiddenCount = items.length - 1;

  // Get class for individual nav items
  const getItemClass = useCallback(
    (path: string, index: number) => {
      const isActive = currentPath === path;
      const isFirstItem = index === 0;
      
      return clsx(
        navButtonBaseClass,
        // Animation classes for non-first items
        !isFirstItem && "nav-group-item",
        isActive
          ? colorConfig.active
          : clsx("border-transparent text-white/60", colorConfig.inactive),
        // First item is always visible, others animate
        !isFirstItem && !isExpanded && "max-w-0 opacity-0 invisible px-0 border-0 overflow-hidden",
        !isFirstItem && isExpanded && "max-w-[200px] opacity-100 visible"
      );
    },
    [currentPath, navButtonBaseClass, colorConfig, isExpanded]
  );

  // Memoize the container class
  const containerClass = useMemo(() => clsx(
    "flex items-center rounded-lg transition-all duration-250 ease-out overflow-visible",
    isExpanded ? "nav-group-expanded bg-white/5 backdrop-blur-sm gap-0.5" : "gap-0"
  ), [isExpanded]);

  // Get first item class (includes indicator styling when collapsed)
  const getFirstItemClass = useCallback(
    (path: string) => {
      const isActive = currentPath === path;
      
      return clsx(
        navButtonBaseClass,
        "flex items-center",
        isActive
          ? colorConfig.active
          : clsx("border-transparent text-white/60", colorConfig.inactive)
      );
    },
    [currentPath, navButtonBaseClass, colorConfig]
  );

  // If only one item, render without animation wrapper
  if (items.length <= 1) {
    const item = items[0];
    if (!item) return null;

    return (
      <NavLink
        to={item.to ?? item.path}
        className={clsx(
          "relative overflow-visible",
          navButtonBaseClass,
          currentPath === item.path
            ? colorConfig.active
            : clsx("border-transparent text-white/60", colorConfig.inactive)
        )}
      >
        {item.icon && showIcons && (
          <span className="hidden 3xl:flex me-1">{item.icon}</span>
        )}
        <span className="text-xs">{item.label}</span>
        {(item.badge ?? 0) > 0 && (
          <span
            className="absolute -top-1.5 -end-1.5 z-10 bg-amber-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] leading-none flex items-center justify-center px-1 ring-2 ring-slate-950"
            title={item.badgeTitle}
          >
            {item.badge}
          </span>
        )}
      </NavLink>
    );
  }

  const firstItem = items[0];

  return (
    <div
      className={containerClass}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="group"
      aria-label={`${groupId} navigation group`}
    >
      {/* First item with chevron indicator when collapsed */}
      <NavLink
        to={firstItem.to ?? firstItem.path}
        className={clsx("relative overflow-visible", getFirstItemClass(firstItem.path))}
      >
        {firstItem.icon && showIcons && (
          <span className="hidden 3xl:flex me-1">{firstItem.icon}</span>
        )}
        <span className="text-xs whitespace-nowrap">{firstItem.label}</span>

        {/* Chevron + count indicator when collapsed */}
        {!isExpanded && hiddenCount > 0 && (
          <span className="flex items-center ms-0.5 text-white/40 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5">
            <span className="text-[10px] font-medium me-0.5">+{hiddenCount}</span>
            <ChevronRightIcon className="w-3 h-3 rtl:rotate-180" />
          </span>
        )}

        {(firstItem.badge ?? 0) > 0 && (
          <span
            className="absolute -top-1.5 -end-1.5 z-10 bg-amber-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] leading-none flex items-center justify-center px-1 ring-2 ring-slate-950"
            title={firstItem.badgeTitle}
          >
            {firstItem.badge}
          </span>
        )}
      </NavLink>

      {/* Remaining items (hidden when collapsed) */}
      {items.slice(1).map((item, index) => (
        <NavLink
          key={item.path}
          to={item.to ?? item.path}
          className={clsx("relative", isExpanded && "overflow-visible", getItemClass(item.path, index + 1))}
          style={{
            // Staggered animation delay for smooth sequential reveal
            transitionDelay: isExpanded ? `${(index + 1) * 30}ms` : "0ms",
          }}
          tabIndex={!isExpanded ? -1 : 0}
        >
          {item.icon && showIcons && (
            <span className="hidden 3xl:flex me-1">{item.icon}</span>
          )}
          <span className="text-xs whitespace-nowrap">{item.label}</span>
          {(item.badge ?? 0) > 0 && (
            <span
              className="absolute -top-1.5 -end-1.5 z-10 bg-amber-500 text-white text-[10px] font-semibold rounded-full h-4 min-w-[16px] leading-none flex items-center justify-center px-1 ring-2 ring-slate-950"
              title={item.badgeTitle}
            >
              {item.badge}
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
};

export default HeaderNavGroup;
